'use client'

import { signUp } from '@repo/auth/client'
import { Eye, EyeOff } from 'lucide-react'
import Link from 'next/link'
import { useEffect, useMemo, useState } from 'react'
import { updateProfileAction } from '@/app/pl/panel/account-actions'
import { FlouWordmark } from '@/components/atoms/flou-wordmark'
import {
  AUTH_ACCENT_GLOW,
  AUTH_ACCENT_GRADIENT,
  AuthCard,
  AuthDivider,
} from '@/components/molecules/auth-card'
import { SocialAuthButtons } from '@/components/molecules/social-auth-buttons.client'
import { useToast } from '@/components/providers/ToastProvider'
import { RegisterCodeStep } from '@/components/sections/register-code-step.client'
import { Check, DocLink, Input } from '@/components/sections/register-fields.client'
import { authErrorMessage } from '@/lib/auth-errors'
import { ACCENT_TEXT, BODY, MICRO, SUBHEADING } from '@/lib/typography'
import { cn } from '@/lib/utils'

type Props = {
  locale: string
  /** Providers the server actually has keys for. Everything else stays "wkrótce". */
  socialProviders?: readonly string[]
}

/**
 * The ticks, addressed once.
 *
 * The markup writes these ids and the adoption effect reads them back; two
 * spellings of the same string would silently reintroduce exactly the defect
 * the effect is there to close.
 */
const CONSENT_IDS = {
  invoice: 'rejestracja-faktura',
  terms: 'rejestracja-regulamin',
  privacy: 'rejestracja-prywatnosc',
} as const

export function RegisterPageView({ locale, socialProviders = [] }: Props) {
  const { toast } = useToast()

  // The accent, the glass and the fields are the sign-in card's — imported,
  // not restated. This page used to hold four private spellings of them
  // (a hand-rolled gradient, a hand-rolled glow, `focus:border-pink-400/60`
  // and `text-pink-300`, none of which matched `/pl/login`), which is how one
  // account surface ends up looking like two products. Owner's call:
  // *„rejestracja ma być w stylu logowania dokładnie"*.

  const [data, setData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirm: '',
    companyName: '',
    taxId: '',
  })
  /**
   * Two ticks, not one. They are two documents with two legal bases — a
   * contract and a processing notice — and the server records them as two
   * timestamps, so asking for them as one box would make "did they accept the
   * terms?" depend on remembering that one flag covered both.
   */
  const [terms, setTerms] = useState(false)
  const [privacy, setPrivacy] = useState(false)
  /**
   * Invoice details, behind a toggle.
   *
   * Most accounts are not a company's, and two empty fields on a registration
   * form are two reasons to hesitate. Asking here rather than at the first
   * order means a business customer is not stopped at checkout by a document
   * they cannot be issued (spec §3).
   */
  const [invoice, setInvoice] = useState(false)
  const [showPwd, setShowPwd] = useState(false)
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)
  /** A refusal from the server, shown in the form rather than in a corner. */
  const [fault, setFault] = useState<string | null>(null)

  const change = (k: keyof typeof data) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setData((d) => ({ ...d, [k]: e.target.value }))

  /** The props every field shares, including the id the adoption below needs. */
  const field = (k: keyof typeof data) => ({
    id: `rejestracja-${k}`,
    onChange: change(k),
  })

  /**
   * Pick up whatever was typed OR TICKED before React attached.
   *
   * The fields are uncontrolled precisely so hydration cannot erase them, but
   * the submit button is gated on `valid`, which is computed from state — so
   * without this a visitor who filled the form during the first second would
   * watch a complete form refuse to submit. Runs once, reads the DOM, and is
   * a no-op in the normal case where nothing was typed that early.
   *
   * ── THE TICKS ARE THE HALF THAT WAS MISSING ──────────────────────────────
   * They were left out of the first version, and they used to be CONTROLLED —
   * so a consent clicked before hydration was worse than lost. React restores
   * a controlled checkbox on hydration, so the flip was wiped before anything
   * could read it: the tick never drew, `terms` stayed false, and the button
   * sat shut over a form that looked complete. Click again and the DOM went
   * back while React finally heard `false` — two clicks, no consent, no
   * explanation. `Check` is uncontrolled now (see `register-fields.client`),
   * which is what leaves a tick here for this effect to find.
   *
   * ── WHY NO E2E CASE PINS THIS ────────────────────────────────────────────
   * Because the suite runs against `next dev`, where it cannot be staged. The
   * production build ships this form in the document — `rejestracja-firstName`
   * and the submit button are both in the server's HTML — so a visitor really
   * can press a consent before React attaches. Under `next dev` the same form
   * does not exist in the DOM until the scripts arrive: measured, the checkbox
   * attaches at 5498 ms and this effect has already run at 5440 ms, so every
   * "press it early" test presses a form that is already live and passes
   * whether or not the bug is present. A test that cannot fail is worse than
   * none, so what stands here instead is this note.
   */
  useEffect(() => {
    const typed: Partial<typeof data> = {}
    for (const key of ['firstName', 'lastName', 'email', 'password', 'confirm'] as const) {
      const node = document.getElementById(`rejestracja-${key}`)
      if (node instanceof HTMLInputElement && node.value) typed[key] = node.value
    }
    if (Object.keys(typed).length > 0) setData((d) => ({ ...d, ...typed }))

    const ticked = (id: string) => {
      const node = document.getElementById(id)
      return node instanceof HTMLInputElement && node.checked
    }
    if (ticked(CONSENT_IDS.terms)) setTerms(true)
    if (ticked(CONSENT_IDS.privacy)) setPrivacy(true)
    if (ticked(CONSENT_IDS.invoice)) setInvoice(true)
  }, [])

  // Ten digits once the dashes and spaces people write NIP-y with are dropped.
  // Checked only when somebody asked for an invoice — letting an incomplete
  // number through means a document that cannot be issued.
  const taxDigits = data.taxId.replace(/\D/g, '')
  const invoiceReady = !invoice || (data.companyName.trim().length > 1 && taxDigits.length === 10)

  const valid = useMemo(() => {
    return (
      data.firstName.trim().length > 1 &&
      data.lastName.trim().length > 1 &&
      /\S+@\S+\.\S+/.test(data.email) &&
      data.password.length >= 8 &&
      data.password === data.confirm &&
      invoiceReady &&
      terms &&
      privacy
    )
  }, [data, terms, privacy, invoiceReady])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!valid || busy) return
    setFault(null)
    setBusy(true)
    const res = await signUp.email({
      email: data.email,
      password: data.password,
      name: `${data.firstName} ${data.lastName}`.trim(),
      // Read by the create hook in `@repo/auth`, which refuses the sign-up
      // without them and stamps the two timestamps off its OWN clock. Nothing
      // here decides when consent happened.
      fetchOptions: { body: { acceptedTerms: true, acceptedPrivacy: true } },
    })
    setBusy(false)
    if (res.error) {
      setFault(authErrorMessage(res.error, 'Spróbuj ponownie.'))
      return
    }
    // Sign-up signed us in, so the profile is writable now. A second call
    // rather than a wider sign-up payload: `companyName` and `taxId` are
    // `input: false` on the auth model on purpose, and the guarded profile
    // use-case is the one place that validates them.
    if (invoice) {
      await updateProfileAction({ companyName: data.companyName.trim(), taxId: taxDigits }).catch(
        () => undefined,
      )
    }

    setDone(true)
    toast({
      kind: 'success',
      title: 'Konto utworzone',
      // The code IS on its way now — `sendVerificationOnSignUp` — so this
      // sentence is finally true. It used to promise a mail nothing sent.
      description: `Wysłaliśmy kod na ${data.email}.`,
    })
  }

  return (
    // The sign-in column, to the pixel: same width, same top, same card, same
    // arrival. `overflow-x-clip` and not `overflow-hidden` — the card's glow
    // reaches past its box, and clipping both axes cuts it along a straight
    // line, which is what makes a glow look like it stopped rather than faded.
    <div className="relative flex w-full flex-1 items-start justify-center overflow-x-clip px-6 pt-1 pb-16">
      <div className="relative z-10 flex w-full max-w-[27rem] flex-col">
        {/* Bigger and tighter, by the owner's note: *„lepiej to poukładaj,
            mniejsze odstępy, większe trochę"*. `SUBHEADING` alone left the
            display face on the body's default line-height, so a two-line
            heading opened a gap wider than the letters are tall; `text-balance`
            then evens the two lines instead of leaving three words alone on the
            second. */}
        {/* TWO EXPLICIT LINES, not a wrap.

            `text-balance` was deciding where this broke, which meant the break
            moved with the viewport and the conjunction „i" kept landing alone
            at the head of the second line with the pink clause behind it — a
            single white letter stranded between two things it belongs to.
            Owner: *„i musi być w nowej linii, bo obecnie jest to sierota,
            mniejszy odstęp między liniami"*.

            So the break is a `block` per clause: line one is the address, line
            two is the promise, at every width. „i" goes INTO the accent, which
            is what removes the stranding — the second line is now one coloured
            phrase rather than a loose letter in front of one.

            The `pb` is not spacing. `background-clip: text` paints only inside
            the element's background box, and at `leading-[1.05]` that box is
            shorter than the glyphs standing in it — the descenders of „yższy"
            would get no gradient and reach the screen sliced off. The negative
            margin gives the space straight back, so the two lines sit where the
            leading says they do. */}
        <h1
          className={cn(
            SUBHEADING,
            'text-center text-[1.75rem] leading-[1.05] text-white sm:text-[2.25rem]',
          )}
        >
          <span className="block">Wejdź do świata FLOU</span>
          <span className={cn(ACCENT_TEXT, 'block pb-[0.14em] -mb-[0.14em]')}>
            i wskocz na wyższy poziom.
          </span>
        </h1>
        <p
          className={cn(BODY, 'mx-auto mt-2 max-w-[23rem] text-center text-balance text-white/60')}
        >
          Zamówienia, terminy, pliki i rozmowy w jednym miejscu.
        </p>

        <AuthCard seeThrough className="flou-auth-enter mt-4 p-6 sm:p-7">
          {/* The mark stands where „Strefa klienta" used to, at the size the
              sign-in card sets. It was `h-11` with a white `drop-shadow` here —
              twice the size and a different light. */}
          <FlouWordmark className="mx-auto h-6 w-auto text-white/90" />

          {done ? (
            <div className="mt-6">
              <RegisterCodeStep email={data.email} locale={locale} />
            </div>
          ) : (
            <>
              <h2 className={cn(BODY, 'mt-6 mb-1 text-center font-semibold text-white')}>
                Załóż konto
              </h2>
              <p className={cn(MICRO, 'mb-5 text-center text-white/55')}>
                Dla twórców i marek rosnących z FLOU
              </p>

              {/* Google first, then the rule, then the form — the sign-in card's
                  order. It used to be the other way round here, so the two
                  pages offered the same two routes in opposite sequence.

                  Google hands back an address it has already verified, so this
                  path skips the code step entirely. The line under it is the
                  consent — there is no checkbox to tick on somebody else's
                  sign-in screen, and the server stamps both timestamps either
                  way. */}
              {/* „Zarejestruj się", not „Zarejestruj się przez":
                  `SocialAuthButtons` renders `{verb} z {label}`, so the longer
                  verb printed „Zarejestruj się przez z Google" on the live
                  button. */}
              <SocialAuthButtons
                verb="Zarejestruj się"
                enabled={socialProviders}
                callbackURL={`/${locale}/panel`}
              />
              <p className={cn(MICRO, 'mt-3 text-center leading-relaxed text-white/55')}>
                Kontynuując przez Google, akceptujesz{' '}
                <DocLink href={`/${locale}/regulamin`}>regulamin</DocLink> i{' '}
                <DocLink href={`/${locale}/polityka-prywatnosci`}>politykę prywatności</DocLink>.
              </p>

              <AuthDivider />

              <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
                {/* The refusal, where the refused thing is — the sign-in card's
                    rule. It was a toast in the corner of the screen, which is
                    read by the people who least need it. */}
                {fault ? (
                  <p
                    role="alert"
                    className={cn(
                      MICRO,
                      'rounded-xl border border-[oklch(0.7_0.2_20)]/35 bg-[oklch(0.55_0.2_20)]/12 px-4 py-3 leading-relaxed text-[oklch(0.9_0.08_20)]',
                    )}
                  >
                    <span className="font-semibold">Nie udało się utworzyć konta.</span> {fault}
                  </p>
                ) : null}

                <div className="grid grid-cols-2 gap-3">
                  <Input label="Imię" {...field('firstName')} autoComplete="given-name" />
                  <Input label="Nazwisko" {...field('lastName')} autoComplete="family-name" />
                </div>
                <Input label="Adres e-mail" type="email" {...field('email')} autoComplete="email" />
                <Input
                  label="Hasło (min. 8 znaków)"
                  type={showPwd ? 'text' : 'password'}
                  {...field('password')}
                  autoComplete="new-password"
                  rightSlot={
                    <button
                      type="button"
                      onClick={() => setShowPwd((v) => !v)}
                      aria-label={showPwd ? 'Ukryj hasło' : 'Pokaż hasło'}
                      // An icon in a 28px box, which clears WCAG 2.5.8. As the
                      // words POKAŻ / UKRYJ it measured fifteen pixels tall —
                      // legible and essentially unhittable with a thumb — and
                      // it was the one control the sign-in card spells
                      // differently.
                      className="flex size-7 items-center justify-center rounded-lg text-white/50 transition-colors hover:text-white"
                    >
                      {showPwd ? (
                        <EyeOff aria-hidden="true" className="size-4" />
                      ) : (
                        <Eye aria-hidden="true" className="size-4" />
                      )}
                    </button>
                  }
                />
                <Input
                  label="Powtórz hasło"
                  type={showPwd ? 'text' : 'password'}
                  {...field('confirm')}
                  autoComplete="new-password"
                  error={
                    data.confirm.length > 0 && data.password !== data.confirm
                      ? 'Hasła nie są zgodne'
                      : undefined
                  }
                />

                {invoice ? (
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      label="Nazwa firmy"
                      {...field('companyName')}
                      autoComplete="organization"
                    />
                    <Input label="NIP" {...field('taxId')} autoComplete="off" />
                  </div>
                ) : null}

                <Check
                  id={CONSENT_IDS.invoice}
                  checked={invoice}
                  onChange={setInvoice}
                  label="Potrzebuję faktury na firmę"
                />

                <Check
                  id={CONSENT_IDS.terms}
                  checked={terms}
                  onChange={setTerms}
                  label={
                    <>
                      Akceptuję <DocLink href={`/${locale}/regulamin`}>regulamin</DocLink>.
                    </>
                  }
                />

                <Check
                  id={CONSENT_IDS.privacy}
                  checked={privacy}
                  onChange={setPrivacy}
                  label={
                    <>
                      Przeczytałem{' '}
                      <DocLink href={`/${locale}/polityka-prywatnosci`}>
                        politykę prywatności
                      </DocLink>{' '}
                      i wiem, jak przetwarzacie moje dane.
                    </>
                  }
                />

                <button
                  type="submit"
                  disabled={!valid || busy}
                  className={cn(
                    BODY,
                    'mt-1 w-full rounded-xl py-3.5 font-semibold transition-all duration-300',
                    valid && !busy
                      ? 'text-white hover:brightness-110 active:scale-[0.99]'
                      : 'cursor-not-allowed bg-white/[0.06] text-white/45',
                  )}
                  style={
                    valid && !busy
                      ? { background: AUTH_ACCENT_GRADIENT, boxShadow: AUTH_ACCENT_GLOW }
                      : undefined
                  }
                >
                  {busy ? 'Tworzę konto…' : 'Zarejestruj się'}
                </button>
              </form>

              <p className={cn(MICRO, 'mt-5 text-center text-white/55')}>
                Masz już konto?{' '}
                <Link
                  href={`/${locale}/login`}
                  className="font-semibold text-white/72 transition-colors hover:text-white"
                >
                  Zaloguj się
                </Link>
              </p>
            </>
          )}
        </AuthCard>
      </div>
    </div>
  )
}
